import axios from 'axios';
import { isSessionStorageSupported } from '../lib/helpers';

import {
  AUTH_METHODS,
  PERSONAL_CHECKS_API,
  SESSION_STORAGE_AUTH_METHOD_KEY,
  SESSION_STORAGE_CANDIDATE_IDS_KEY,
  SESSION_STORAGE_JWT_KEY,
  SESSION_STORAGE_TOKEN_KEY,
} from '../constants';
import { trackAnalyticsEvent } from './analytics';
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from '../lib/analytics';
import { nonCandidateLoggedIn, resetFailedPortal } from './reports';
import { isWorkosDobPending } from './workosLogin';
import { clearUnifiedLoginSession } from '../utils/UnifiedLogin';
import {
  beginAuthSessionAttempt,
  hasNewerAuthSessionAttempt,
  isStaleAuthSessionAttempt,
  invalidateAuthSessionAttempts,
} from './authSessionGeneration';
import { AUTH_TIMEOUT_MS } from '../utils/authConstants';

/*
 * Action Types
 */

export const CHECK_FOR_ACTIVE_AUTH = 'CHECK_FOR_ACTIVE_AUTH';
export const checkForActiveAuth = () => ({
  type: CHECK_FOR_ACTIVE_AUTH,
});

export const CHECK_FOR_ACTIVE_AUTH_COMPLETE = 'CHECK_FOR_ACTIVE_AUTH_COMPLETE';
export const checkForActiveAuthComplete = payload => ({
  type: CHECK_FOR_ACTIVE_AUTH_COMPLETE,
  payload,
});

// A session check that restores a candidate also clears a stale failed report
// fetch (see resetFailedPortal). Explicit here, not a portal reducer case on
// CHECK_FOR_ACTIVE_AUTH_COMPLETE, so it survives auth leaving Redux.
export const completeActiveAuthCheck = payload => dispatch => {
  const result = dispatch(checkForActiveAuthComplete(payload));
  if (payload?.loggedIn) {
    dispatch(resetFailedPortal());
  }
  return result;
};

/*
 * Actions
 */

const CANDIDATE_PORTAL_AUTH_PATH = '/candidate_portal_auth';

const fetchCheckrProfileAuthSession = () => {
  const url = new URL(CANDIDATE_PORTAL_AUTH_PATH, PERSONAL_CHECKS_API);
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  }).then(async res => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(body?.error || res.statusText);
      err.status = res.status;
      throw err;
    }
    if (!body?.token) throw new Error(body?.error || 'No token');
    return {
      token: body.token,
      candidate_ids: body.candidate_ids || [],
    };
  });
};

export const clearCheckrProfileAuthSession = () => {
  const url = new URL(
    CANDIDATE_PORTAL_AUTH_PATH,
    PERSONAL_CHECKS_API,
  ).toString();
  return axios.delete(url, { withCredentials: true }).catch(() => {});
};

/** Returned when an attempt was superseded, so callers stop rather than fall through. */
const STALE = Symbol('stale-auth-session-attempt');

const loggedOutPayload = () => ({
  loggedIn: false,
  candidateJwt: '',
  candidateIds: [],
});

const AUTH_SESSION_STORAGE_KEYS = [
  SESSION_STORAGE_JWT_KEY,
  SESSION_STORAGE_CANDIDATE_IDS_KEY,
  SESSION_STORAGE_AUTH_METHOD_KEY,
  SESSION_STORAGE_TOKEN_KEY,
];

const clearAuthSessionStorage = () => {
  for (const key of AUTH_SESSION_STORAGE_KEYS) {
    globalThis.sessionStorage.removeItem(key);
  }
  // Provenance is part of the auth session, not a separate lifetime. Leaving
  // the marker behind lets a later legacy session log out through a broker it
  // never authenticated with.
  clearUnifiedLoginSession();
};

const dispatchCheckrProfileLoginSuccess = (
  dispatch,
  { candidateJwt, candidateIds },
) => {
  dispatch(
    trackAnalyticsEvent(
      ANALYTICS_EVENTS.CANDIDATE_PORTAL_CHECKR_PROFILE_AUTH_LOGIN_SUCCESS,
      { [ANALYTICS_PROPERTIES.AUTH_METHOD]: AUTH_METHODS.CHECKR_PROFILE },
    ),
  );

  return dispatch(
    completeActiveAuthCheck({
      loggedIn: true,
      candidateJwt,
      candidateIds,
      authMethod: AUTH_METHODS.CHECKR_PROFILE,
    }),
  );
};

// A legacy check (`attempt` null) is never superseded, as on master.
const isSuperseded = attempt =>
  attempt !== null && isStaleAuthSessionAttempt(attempt);

const restoreExternalAuthSession = async (dispatch, attempt) => {
  try {
    const { token, candidate_ids } = await fetchCheckrProfileAuthSession();

    if (isSuperseded(attempt)) return STALE;

    // Non-candidate: API returned a token but no candidate records.
    // Clear all sessionStorage auth keys so the next page load doesn't
    // trigger "session expired" detection (previousAuthMethod && !existingSession).
    // The cookie persists independently, so fetchCheckrProfileAuthSession will
    // re-authenticate on the next full page load.
    if (!candidate_ids || candidate_ids.length === 0) {
      if (isSessionStorageSupported()) {
        clearAuthSessionStorage();
      }

      dispatchCheckrProfileLoginSuccess(dispatch, {
        candidateJwt: '',
        candidateIds: [],
      });
      return dispatch(nonCandidateLoggedIn());
    }

    if (isSessionStorageSupported()) {
      globalThis.sessionStorage.setItem(SESSION_STORAGE_JWT_KEY, token);
      globalThis.sessionStorage.setItem(
        SESSION_STORAGE_CANDIDATE_IDS_KEY,
        Array.isArray(candidate_ids) ? candidate_ids.join(',') : '',
      );
      globalThis.sessionStorage.setItem(
        SESSION_STORAGE_AUTH_METHOD_KEY,
        AUTH_METHODS.CHECKR_PROFILE,
      );
    }

    return dispatchCheckrProfileLoginSuccess(dispatch, {
      candidateJwt: token,
      candidateIds: candidate_ids || [],
    });
  } catch {
    if (isSuperseded(attempt)) return STALE;

    // No broker probe here, deliberately. The broker hands back its own
    // tenant access token, which is not the AMS candidate JWT that
    // `candidate-portal-bff` validates as `Authz::CANDIDATE`, so adopting it
    // as `candidate_jwt` would 401 every report fetch. Inbound restore waits
    // for a `/exchange` (or a sealed `.checkr.com` cookie) that yields that
    // JWT — see ECDEX-3368 and the discussion on !3291.
    return dispatch(
      completeActiveAuthCheck({
        loggedIn: false,
        candidateJwt: '',
        candidateIds: [],
      }),
    );
  }
};

/**
 * `inbound` is on only when the unified-login session-management flag is.
 * Without it the check runs exactly as before unified login: no deadline, and
 * nothing can supersede it.
 */
export const checkForActiveAuthSession =
  ({ inbound = false } = {}) =>
  dispatch =>
    inbound
      ? checkInboundAuthSession(dispatch)
      : runAuthSessionCheck(dispatch, { attempt: null });

const checkInboundAuthSession = async dispatch => {
  // Opening an attempt supersedes any still in flight, so a second bootstrap
  // (a route transition, say) cannot have its late result overwrite this one.
  const attempt = beginAuthSessionAttempt();

  // The flag backstop bounds only the Harness decision; the network work
  // below is unbounded. Give the whole attempt a deadline, after which it is
  // marked stale and the legacy path is taken.
  //
  // An attempt superseded without a successor (unmount, logout) must still
  // settle the check, logged out and without touching sessionStorage, or
  // `checkedAuth` stays false. That happens on whichever comes first: the late
  // STALE result or the deadline (which also covers a probe that never
  // answers). A newer attempt settles the check itself; the reducer keeps a
  // logged-in state from being overwritten.
  let settled = false;
  const settleLoggedOut = () => {
    settled = true;
    return dispatch(completeActiveAuthCheck(loggedOutPayload()));
  };
  const deadline = globalThis.setTimeout(() => {
    if (settled || hasNewerAuthSessionAttempt(attempt)) return;
    invalidateAuthSessionAttempts();
    settleLoggedOut();
  }, AUTH_TIMEOUT_MS);

  try {
    const result = await runAuthSessionCheck(dispatch, { attempt });
    if (result === STALE && !settled && !hasNewerAuthSessionAttempt(attempt)) {
      return settleLoggedOut();
    }
    return result;
  } finally {
    globalThis.clearTimeout(deadline);
  }
};

const runAuthSessionCheck = async (dispatch, { attempt }) => {
  dispatch(checkForActiveAuth());

  if (!isSessionStorageSupported()) {
    return dispatch(
      completeActiveAuthCheck({
        loggedIn: false,
        candidateJwt: '',
        candidateIds: [],
      }),
    );
  }

  const existingSession = globalThis.sessionStorage.getItem(
    SESSION_STORAGE_JWT_KEY,
  );
  const existingCandidateIds = globalThis.sessionStorage.getItem(
    SESSION_STORAGE_CANDIDATE_IDS_KEY,
  );

  if (existingSession) {
    const candidateJwt = existingSession;
    const candidateIds = existingCandidateIds
      ? existingCandidateIds.split(',')
      : [];
    const authMethod =
      globalThis.sessionStorage.getItem(SESSION_STORAGE_AUTH_METHOD_KEY) ||
      null;
    return dispatch(
      completeActiveAuthCheck({
        loggedIn: true,
        candidateJwt,
        candidateIds,
        authMethod,
      }),
    );
  }

  const previousAuthMethod = globalThis.sessionStorage.getItem(
    SESSION_STORAGE_AUTH_METHOD_KEY,
  );
  // Reached only when existingSession is falsy (the block above returns early
  // otherwise), so both branches here run without a live JWT.
  if (previousAuthMethod && isWorkosDobPending()) {
    return dispatch(
      completeActiveAuthCheck({
        loggedIn: false,
        candidateJwt: '',
        candidateIds: [],
        authMethod: previousAuthMethod,
      }),
    );
  }
  if (previousAuthMethod) {
    clearAuthSessionStorage();
    dispatch(
      trackAnalyticsEvent(ANALYTICS_EVENTS.CANDIDATE_PORTAL_SESSION_EXPIRED, {
        [ANALYTICS_PROPERTIES.AUTH_METHOD]: previousAuthMethod,
      }),
    );
    return dispatch(
      completeActiveAuthCheck({
        loggedIn: false,
        candidateJwt: '',
        candidateIds: [],
      }),
    );
  }

  return restoreExternalAuthSession(dispatch, attempt);
};
